import React, { useEffect, useState } from 'react';

/**
 * In-app confirm modal (replaces window.confirm). Matches Daypop cards: white panel, gold accents, border-border.
 */
const ConfirmDialog = ({
  open,
  onClose,
  title,
  message,
  detail,
  confirmLabel = 'Delete',
  cancelLabel = 'Cancel',
  onConfirm,
}) => {
  const [pending, setPending] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape' && !pending) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, pending]);

  useEffect(() => {
    if (!open) setPending(false);
  }, [open]);

  if (!open) return null;

  const handleConfirm = async () => {
    setPending(true);
    try {
      await onConfirm();
      onClose();
    } catch {
      /* Keep dialog open; parent may toast */
    } finally {
      setPending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm animate-overlay-in"
      role="presentation"
      onClick={() => !pending && onClose()}
    >
      <div
        className="bg-bg-surface rounded-2xl border border-border shadow-lg max-w-md w-full p-8 animate-modal-in"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-desc"
        onClick={(e) => e.stopPropagation()}
      >
        <h2
          id="confirm-dialog-title"
          className="font-display text-2xl font-bold text-text-primary mb-3"
        >
          {title}
        </h2>
        <div id="confirm-dialog-desc" className="space-y-3">
          {message ? (
            <p className="text-text-secondary text-sm leading-relaxed">{message}</p>
          ) : null}
          {detail ? (
            <p className="text-sm text-text-tertiary italic border-l-2 border-gold pl-3 py-1 line-clamp-4">
              {detail}
            </p>
          ) : null}
        </div>
        <div className="flex gap-3 mt-8">
          <button
            type="button"
            disabled={pending}
            onClick={onClose}
            className="flex-1 py-3 px-4 border-2 border-border text-text-secondary rounded-xl font-medium hover:border-gold hover:text-text-primary transition-colors disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            disabled={pending}
            onClick={handleConfirm}
            className="flex-1 py-3 px-4 bg-error hover:bg-error/90 text-white rounded-xl font-semibold transition-colors disabled:opacity-60"
            data-testid="confirm-dialog-confirm"
          >
            {pending ? '…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmDialog;
